import classNames from 'classnames'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { useMyMonitoringCreateControl, useMyMonitoringUpdateControl } from '@apps-common/apis/my-monitoring/control'
import { useCreateUnitControl, useUpdateUnitControl } from '@apps-common/apis/portal/control'
import {
  ControlAlarmStatesForm,
  ControlCustomStatesForm
} from '@apps-common/apps/portal/pages/units/pages/manage/pages/controls/pages/modify/components'
import { TestOutputModal } from '@apps-common/apps/portal/pages/units/pages/manage/pages/controls/pages/modify/modals/TestOutputModal'
import { CardContainer } from '@apps-common/components/common'
import { SectionTitle } from '@apps-common/components/common/portal-title'
import { ButtonRoundSm } from '@apps-common/components/elements'
import { InputSM, SelectInputSM } from '@apps-common/components/elements/form'
import { VertSpacerLg } from '@apps-common/components/layout'
import { MODAL_BUTTON_ROUNDED_STYLES, ModalButton } from '@apps-common/components/modals'
import { CID_EVENT_CODES } from '@apps-common/config/cid-event-codes'
import {
  CONTROL_COMMAND_SELECT_OPTIONS,
  CONTROL_OUTPUT_NUMBER_SELECT_OPTIONS,
  CONTROL_STATE_OFF,
  CONTROL_STATE_ON,
  CONTROL_TRIGGER_TYPE_LATCH,
  CONTROL_TRIGGER_TYPE_PULSE,
  CONTROL_TRIGGER_TYPE_SELECT_OPTIONS,
  CONTROL_TYPE_ALARM,
  CONTROL_TYPE_SELECT_OPTIONS,
  TRK_OUTPUT_ON_TIMES
} from '@apps-common/config/controls'
import { FORM_CONTROL_MODIFY_ID } from '@apps-common/config/form'
import { commonIcons } from '@apps-common/config/icons'
import { FOURGT_CODE } from '@apps-common/config/product-codes'
import { FORM_DEFAULT_REQUIRED } from '@apps-common/config/react-hook-form'
import { useProjectHook } from '@apps-common/hooks/useProjectHook'
import {
  IEventCheckModel,
  IUnitCommandModel,
  IUnitControlCompleteEditModel,
  IUnitControlStateEditModel
} from '@apps-common/types/portal/unit-control'

const buildRemovedControlStates = (states: IUnitControlStateEditModel[], original?: IUnitControlStateEditModel[]) => {
  if (!original) return []

  return original.filter((o) => states.findIndex((s) => s.unitControlStateId === o.unitControlStateId) === -1)
}

const buildRemovedEventChecks = (states: IUnitControlStateEditModel[], original?: IUnitControlStateEditModel[]) => {
  if (!original) return []

  const currentEcs = states.reduce((acc: IEventCheckModel[], curr) => [...acc, ...(curr?.eventChecks || [])], [])
  const originalEcs = original.reduce((acc: IEventCheckModel[], curr) => [...acc, ...(curr?.eventChecks || [])], [])

  return originalEcs.filter((o) => currentEcs.findIndex((c) => c.eventCheckId === o.eventCheckId) === -1)
}

const buildControlFromFormProps = (
  unitId: string,
  model: InputProps,
  original?: IUnitControlCompleteEditModel
): IUnitControlCompleteEditModel => {
  const { controlName, controlTypeId, outputNumber, triggerType, onTime, onCommand, offCommand, alarmStates, customStates } = model
  const { control, commands, controlStates } = original || {}

  const onCmd = commands?.find((c) => c.commandState?.toLowerCase() === CONTROL_STATE_ON)
  const offCmd = commands?.find((c) => c.commandState?.toLowerCase() === CONTROL_STATE_OFF)
  let offCmdValue = onCommand === CONTROL_STATE_ON ? CONTROL_STATE_OFF : CONTROL_STATE_ON

  if (triggerType === CONTROL_TRIGGER_TYPE_LATCH.value) {
    offCmdValue = offCommand
  }

  const updatedCommands: IUnitCommandModel[] = [
    {
      id: onCmd?.id || 0,
      unitControlId: control?.unitControlId || 0,
      commandState: CONTROL_STATE_ON,
      command: outputNumber,
      value: onCommand
    },
    {
      id: offCmd?.id || 0,
      unitControlId: control?.unitControlId || 0,
      commandState: CONTROL_STATE_OFF,
      command: outputNumber,
      value: offCmdValue
    }
  ]

  let updatedStates = []

  if (controlTypeId === CONTROL_TYPE_ALARM) {
    updatedStates = alarmStates
  } else {
    updatedStates = customStates
  }

  return {
    control: {
      unitControlId: control?.unitControlId || 0,
      controlTypeId,
      controlName,
      triggerType,
      onTime,
      unitId,
      requiresControlState: controlTypeId === CONTROL_TYPE_ALARM || customStates?.length > 0,
      userId: 0,
      dateEdited: new Date()
    },
    commands: updatedCommands,
    controlStates: updatedStates,
    removedControlStates: buildRemovedControlStates(updatedStates, controlStates),
    removedEventChecks: buildRemovedEventChecks(updatedStates, controlStates)
  }
}

const buildFormPropsFromControl = (model?: IUnitControlCompleteEditModel): InputProps | undefined => {
  if (!model) {
    return {
      controlName: '',
      controlTypeId: CONTROL_TYPE_ALARM,
      outputNumber: 'output1',
      triggerType: CONTROL_TRIGGER_TYPE_PULSE.value as string,
      onTime: 2000,
      onCommand: CONTROL_STATE_ON,
      offCommand: CONTROL_STATE_OFF,
      alarmStates: [],
      customStates: []
    }
  }

  const { control, commands, controlStates } = model
  const { controlName, controlTypeId, triggerType, onTime } = control
  const onCommand = commands?.find((c) => c.commandState?.toLowerCase() === CONTROL_STATE_ON)
  const offCommand = commands?.find((c) => c.commandState?.toLowerCase() === CONTROL_STATE_OFF)

  let alarmStates: IUnitControlStateEditModel[] = []
  let customStates: IUnitControlStateEditModel[] = []

  if (controlTypeId === CONTROL_TYPE_ALARM) {
    alarmStates = controlStates?.map((s) => {
      const { unitControlStateId, unitControlId, stateName, alarmPartition, userId, dateEdited, eventChecks } = s

      return {
        unitControlStateId,
        unitControlId,
        stateName,
        alarmPartition,
        userId,
        dateEdited,
        eventChecks: []
      }
    })
  } else {
    customStates = controlStates?.map((s) => {
      const { unitControlStateId, unitControlId, stateName, alarmPartition, userId, dateEdited, eventChecks } = s

      return {
        unitControlStateId,
        unitControlId,
        stateName,
        alarmPartition,
        userId,
        dateEdited,
        eventChecks: (eventChecks || []).map((ec) => ({
          ...ec,
          description: CID_EVENT_CODES.find((c) => c.code === ec.eventCode)?.description || ''
        }))
      }
    })
  }

  return {
    controlName,
    controlTypeId,
    outputNumber: onCommand?.command || offCommand?.command || 'output1',
    triggerType,
    onTime,
    onCommand: onCommand?.value?.toLowerCase() || 'on',
    offCommand: offCommand?.value?.toLowerCase() || 'off',
    alarmStates,
    customStates
  }
}

interface InputProps {
  controlName: string
  controlTypeId: number
  outputNumber: string
  triggerType: string
  onTime: number
  onCommand: string
  offCommand: string
  alarmStates: IUnitControlStateEditModel[]
  customStates: IUnitControlStateEditModel[]
}

interface IProps {
  unitId: string
  productId: string | undefined
  isActivated?: boolean
  loading: boolean
  control?: IUnitControlCompleteEditModel
  onSave: () => void
  onUpdating: (value: boolean) => void
}

export const ControlModifyForm: React.FC<IProps> = ({ unitId, productId, isActivated, loading, control, onSave, onUpdating }) => {
  const useCreateHook = useProjectHook(useCreateUnitControl, useMyMonitoringCreateControl)
  const useUpdateHook = useProjectHook(useUpdateUnitControl, useMyMonitoringUpdateControl)
  const btnColor = useProjectHook('bg-brand text-on-brand', 'bg-mm-brand-2 text-on-mm-brand-2')

  const { mutate: createMutation, isPending: createIsPending } = useCreateHook(
    true,
    () => onSave(),
    () => onUpdating(false)
  )
  const { mutate: updateMutation, isPending: updateIsPending } = useUpdateHook(
    true,
    () => onSave(),
    () => onUpdating(false)
  )

  const methods = useForm<InputProps>({
    mode: 'onChange',
    reValidateMode: 'onChange'
  })
  const { register, handleSubmit, formState, watch, reset } = methods
  const { isSubmitting, errors } = formState

  useEffect(() => {
    const formControl = buildFormPropsFromControl(control)

    reset(formControl)
  }, [control])

  const outputNumber = watch('outputNumber')
  const triggerType = watch('triggerType')
  const onTime = watch('onTime')
  const onCommand = watch('onCommand')
  const offCommand = watch('offCommand')

  const onSubmit = (values: InputProps) => {
    const data = buildControlFromFormProps(unitId, values, control)

    onUpdating(true)

    if (control) {
      updateMutation({ model: data })
    } else {
      createMutation({ model: data })
    }
  }

  const inputsDisabled = loading || isSubmitting || createIsPending || updateIsPending

  return (
    <div>
      <FormProvider {...methods}>
        <form id={FORM_CONTROL_MODIFY_ID} noValidate onSubmit={handleSubmit(onSubmit)}>
          <div className='flex flex-col gap-[20px]'>
            <CardContainer bgClasses='bg-surface border border-border'>
              <SectionTitle text='Details' classes='ml-[10px]' />
              <VertSpacerLg />
              <div>
                <InputSM
                  type='text'
                  placeholder='Enter the control name e.g. Alarm'
                  label='Control name'
                  id='controlName'
                  showLabel
                  loading={loading}
                  disabled={inputsDisabled}
                  registration={register('controlName', { required: FORM_DEFAULT_REQUIRED })}
                  errors={errors.controlName}
                />
                <SelectInputSM
                  type='text'
                  label='Control type'
                  id='controlType'
                  showLabel
                  loading={loading}
                  disabled={inputsDisabled}
                  registration={register('controlTypeId', {
                    required: FORM_DEFAULT_REQUIRED,
                    valueAsNumber: true
                  })}
                  options={CONTROL_TYPE_SELECT_OPTIONS?.map((ct) => ({ label: ct.label, value: ct.value })) || []}
                  errors={errors.controlTypeId}
                />
              </div>
            </CardContainer>
            <CardContainer bgClasses='bg-surface border border-border'>
              <div className='flex items-center justify-between'>
                <SectionTitle text='Output' classes='ml-[10px]' />
                <ModalButton
                  classes={classNames(MODAL_BUTTON_ROUNDED_STYLES, 'bg-secondary-content/[0.04]')}
                  component={TestOutputModal}
                  disabled={inputsDisabled || !isActivated}
                  componentProps={{
                    unitId,
                    triggerType,
                    onTime,
                    outputNumber,
                    onCommand,
                    offCommand
                  }}
                >
                  <ButtonRoundSm type='div' className={btnColor}>
                    <commonIcons.testOutput />
                  </ButtonRoundSm>
                </ModalButton>
              </div>
              <VertSpacerLg />
              <div>
                <SelectInputSM
                  type='text'
                  placeholder='Output number'
                  label='Output number'
                  id='outputNumber'
                  showLabel
                  loading={loading}
                  disabled={inputsDisabled}
                  registration={register('outputNumber', {
                    required: FORM_DEFAULT_REQUIRED
                  })}
                  options={CONTROL_OUTPUT_NUMBER_SELECT_OPTIONS?.map((ct) => ({ label: ct.label, value: ct.value })) || []}
                  errors={errors.outputNumber}
                />
                <SelectInputSM
                  type='text'
                  placeholder='Trigger type'
                  label='Trigger Type'
                  id='triggerType'
                  showLabel
                  loading={loading}
                  disabled={inputsDisabled}
                  registration={register('triggerType', {
                    required: FORM_DEFAULT_REQUIRED
                  })}
                  options={CONTROL_TRIGGER_TYPE_SELECT_OPTIONS}
                  errors={errors.triggerType}
                />
                {productId === FOURGT_CODE && (
                  <SelectInputSM
                    type='text'
                    placeholder='On time'
                    label='On time'
                    id='onTime'
                    showLabel
                    loading={loading}
                    disabled={inputsDisabled}
                    registration={register('onTime', {
                      required: FORM_DEFAULT_REQUIRED,
                      valueAsNumber: true
                    })}
                    options={TRK_OUTPUT_ON_TIMES}
                    errors={errors.onTime}
                  />
                )}
                <SelectInputSM
                  type='text'
                  placeholder='On command'
                  label='On command'
                  id='onCommand'
                  showLabel
                  loading={loading}
                  disabled={inputsDisabled}
                  registration={register('onCommand', {
                    required: true
                  })}
                  options={CONTROL_COMMAND_SELECT_OPTIONS}
                  errors={errors.onCommand}
                />
                {triggerType === CONTROL_TRIGGER_TYPE_LATCH.value && (
                  <SelectInputSM
                    type='text'
                    placeholder='Off command'
                    label='Off command'
                    id='offCommand'
                    showLabel
                    loading={loading}
                    disabled={inputsDisabled}
                    registration={register('offCommand', {
                      required: FORM_DEFAULT_REQUIRED
                    })}
                    options={[...CONTROL_COMMAND_SELECT_OPTIONS].reverse()}
                    errors={errors.offCommand}
                  />
                )}
              </div>
            </CardContainer>
            <div>
              <ControlAlarmStatesForm loading={loading} disabled={inputsDisabled} />
              <ControlCustomStatesForm loading={loading} disabled={inputsDisabled} />
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  )
}
